import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@stockline/ui/components/dialog";
import { Input } from "@stockline/ui/components/input";
import { Button } from "@stockline/ui/components/button";
import type { CustomEntity } from "@/app/api/types/preOrder";

interface CustomEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: CustomEntity | null;
  onSave: (entity: CustomEntity) => void;
  title: string;
  /** Visible helper line under the title. Falls back to an sr-only default. */
  description?: string;
}

export function CustomEntityDialog({
  open,
  onOpenChange,
  entity,
  onSave,
  title,
  description,
}: CustomEntityDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(entity?.name || "");
      setAddress1(entity?.address_1 || "");
      setAddress2(entity?.address_2 || "");
      setPostalCode(entity?.code_postal || "");
      setCity(entity?.ville || "");
      setCountry(entity?.pays || "");
    }
  }, [open, entity]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      address_1: address1.trim() || null,
      address_2: address2.trim() || null,
      code_postal: postalCode.trim() || null,
      ville: city.trim() || null,
      pays: country.trim() || null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="custom-entity-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className={description ? undefined : "sr-only"}>
            {description ??
              t(
                "common.customEntityDescription",
                "Enter details for a custom entity not in the database",
              )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          <div>
            <label className="text-sm font-medium">
              {t("common.name", "Name")} *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("common.name", "Name")}
              autoFocus
              data-testid="custom-entity-name-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium">
              {t("partners.address1", "Address 1")}
            </label>
            <Input
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              placeholder={t("partners.address1", "Address 1")}
            />
          </div>
          <div>
            <label className="text-sm font-medium">
              {t("partners.address2", "Address 2")}
            </label>
            <Input
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
              placeholder={t("partners.address2", "Address 2")}
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-sm font-medium">
                {t("partners.postalCode", "Postal code")}
              </label>
              <Input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder={t("partners.postalCode", "Postal code")}
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium">
                {t("partners.city", "City")}
              </label>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t("partners.city", "City")}
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">
              {t("partners.country", "Country")}
            </label>
            <Input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder={t("partners.country", "Country")}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim()}
            data-testid="custom-entity-save"
          >
            {t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
